import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { User } from '@/contexts/AuthContext';
import { signInWithCredential, GoogleAuthProvider } from 'firebase/auth';
import { auth } from '@/firebase/config';
import { getInvitationByToken } from '@/services/invitationService';
import { redirectToMainDomain, getLoginUrl } from '@/utils/domainUtils';
import { trackAuth } from '@/utils/analytics';

declare global {
  interface Window {
    google: {
      accounts: {
        id: {
          initialize: (config: unknown) => void;
          prompt: () => void;
          disableAutoSelect: () => void;
          renderButton: (element: HTMLElement, config: any) => void;
        };
      };
    };
  }
}

interface GoogleAuthProps {
  invitationToken?: string | null;
  isSignUpMode?: boolean;
}

const GoogleAuth: React.FC<GoogleAuthProps> = ({ invitationToken: propInvitationToken, isSignUpMode = false }) => {
  const { login, logout, isAuthenticated, user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const searchParams = useSearchParams()[0];
  
  // Get invitation token from props or URL
  const invitationToken = propInvitationToken || searchParams.get('invitation');

  const handleCredentialResponse = useCallback(async (response: { credential: string }) => {
    setIsLoading(true);
    
    try {
      // Check if Firebase Auth is available
      console.log('🔍 Auth object:', auth);
      if (!auth) {
        console.warn('⚠️ Firebase Auth not initialized, using fallback method');
        throw new Error('Firebase Auth not available');
      }
      
      // Sign in with Firebase Auth using the credential
      const credential = GoogleAuthProvider.credential(response.credential);
      console.log('🔍 Attempting Firebase Auth sign-in...');
      const userCredential = await signInWithCredential(auth, credential);
      const firebaseUser = userCredential.user;
      
      console.log('✅ Firebase Auth successful:', firebaseUser.uid);
      console.log('✅ Firebase Auth user:', {
        uid: firebaseUser.uid,
        email: firebaseUser.email,
        name: firebaseUser.displayName
      });
      
      // Check for invitation token and process if needed
      if (invitationToken && firebaseUser.email) {
        try {
          const invitationResult = await getInvitationByToken(invitationToken);
          if (invitationResult.success && invitationResult.invitation) {
            const invitation = invitationResult.invitation;
            // Check if email matches invitation
            if (invitation.email.toLowerCase() === firebaseUser.email?.toLowerCase()) {
              // Get or create user document in Firestore
              const { doc, getDoc, setDoc, updateDoc, serverTimestamp } = await import('firebase/firestore');
              const { db } = await import('@/firebase/config');
              
              const userDocRef = doc(db, 'users', firebaseUser.uid);
              const userDoc = await getDoc(userDocRef);
              
              if (!userDoc.exists()) {
                // Create user document with invitation data
                const userRoles = invitation.roles || [invitation.role];
                const primaryRole = invitation.role;
                
                const userData: any = {
                  id: firebaseUser.uid,
                  name: firebaseUser.displayName || invitation.name,
                  email: firebaseUser.email,
                  role: primaryRole,  // Keep for backward compatibility
                  roles: userRoles,   // Keep for backward compatibility
                  createdAt: serverTimestamp(),
                  updatedAt: serverTimestamp(),
                  authProvider: 'google'
                };
                
                // Add organization data if available
                if (invitation.organizationId) {
                  userData.organizationId = invitation.organizationId;
                  userData.organizationIds = [invitation.organizationId];
                  
                  // Create organizationRoles structure with new nested format
                  const { SYSTEM_ROLES, OWNER_ROLES } = await import('@/constants/roles');
                  const ownerRoles = userRoles.filter((role: string) => OWNER_ROLES.includes(role as any));
                  const validSystemRole = SYSTEM_ROLES.includes(primaryRole as any) ? primaryRole : 'user';
                  
                  userData.organizationRoles = {
                    [invitation.organizationId]: {
                      systemRole: validSystemRole,
                      ownerRoles: ownerRoles
                    }
                  };
                }
                
                await setDoc(userDocRef, userData);
                
                // Mark invitation as accepted
                const invitationRef = doc(db, 'invitations', invitation.id);
                await updateDoc(invitationRef, {
                  status: 'accepted',
                  acceptedAt: serverTimestamp()
                });
                
                console.log('✅ Invitation accepted via Google OAuth');
              } else {
                // User exists - add new organization to their list
                const existingUserData = userDoc.data();
                const existingOrgIds = existingUserData.organizationIds || [];
                
                // If user has a single organizationId (backward compatibility), add it to array
                if (existingUserData.organizationId && !existingOrgIds.includes(existingUserData.organizationId)) {
                  existingOrgIds.push(existingUserData.organizationId);
                }
                
                // Add new organization to the array if it's not already there
                if (invitation.organizationId && !existingOrgIds.includes(invitation.organizationId)) {
                  existingOrgIds.push(invitation.organizationId);
                  
                  // Build or update organizationRoles structure
                  let organizationRoles: { [key: string]: { systemRole: string; ownerRoles: string[] } } = {};
                  
                  // Get existing organizationRoles if any
                  if (existingUserData.organizationRoles && typeof existingUserData.organizationRoles === 'object') {
                    organizationRoles = { ...existingUserData.organizationRoles };
                  }
                  
                  // Add roles for the new organization from invitation
                  const userRoles = invitation.roles || [invitation.role];
                  const primaryRole = invitation.role;
                  const { SYSTEM_ROLES, OWNER_ROLES } = await import('@/constants/roles');
                  const ownerRoles = userRoles.filter((role: string) => OWNER_ROLES.includes(role as any));
                  const validSystemRole = SYSTEM_ROLES.includes(primaryRole as any) ? primaryRole : 'user';
                  
                  organizationRoles[invitation.organizationId] = {
                    systemRole: validSystemRole,
                    ownerRoles: ownerRoles
                  };
                  
                  const updateData: any = {
                    organizationIds: existingOrgIds,
                    // Only set organizationId if user doesn't have one yet
                    ...(invitation.organizationId && !existingUserData.organizationId && { organizationId: invitation.organizationId }),
                    updatedAt: serverTimestamp()
                  };
                  
                  // Only update organizationRoles if we have at least one organization
                  if (Object.keys(organizationRoles).length > 0) {
                    updateData.organizationRoles = organizationRoles;
                  }
                  
                  await updateDoc(userDocRef, updateData);
                  
                  // Mark invitation as accepted
                  const invitationRef = doc(db, 'invitations', invitation.id);
                  await updateDoc(invitationRef, {
                    status: 'accepted',
                    acceptedAt: serverTimestamp()
                  });
                  
                  console.log('✅ Added new organization to existing user via Google OAuth');
                }
              }
            }
          }
        } catch (invError) {
          console.error('Error processing invitation:', invError);
          // Continue with login even if invitation processing fails
        }
      }
      
      // Get user data from Firestore
      const { doc: userDocFn, getDoc: getUserDoc, setDoc, serverTimestamp } = await import('firebase/firestore');
      const { db } = await import('@/firebase/config');
      const { userService } = await import('@/firebase/database');
      const userDocRef = userDocFn(db, 'users', firebaseUser.uid);
      const userDoc = await getUserDoc(userDocRef);
      
      // If user doesn't exist in Firestore, create the user document
      // (This handles both signup and first-time login scenarios)
      let userDocData;
      if (!userDoc.exists() && !invitationToken) {
        console.log('🆕 Creating new user document (first-time Google login/signup)...');
        await userService.createUser(firebaseUser.uid, {
          id: firebaseUser.uid,
          name: firebaseUser.displayName || '',
          email: firebaseUser.email || '',
          picture: firebaseUser.photoURL || '',
          role: 'admin', // New signups become admin (they'll create their own org)
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          authProvider: 'google'
        });
        // Reload user document to get fresh data
        const refreshedUserDoc = await getUserDoc(userDocRef);
        userDocData = refreshedUserDoc.exists() ? refreshedUserDoc.data() : {};
        console.log('✅ User document created successfully');
      } else if (!userDoc.exists() && invitationToken) {
        // User doesn't exist but has invitation - will be handled by invitation processing above
        userDocData = {};
      } else {
        userDocData = userDoc.exists() ? userDoc.data() : {};
        console.log('✅ User document already exists in Firestore');
      }
      
      // For invited users, use Firestore verified_email flag (already verified via invitation)
      // For regular users, use Firebase Auth emailVerified status
      // Google OAuth users are typically already verified by Google
      const isVerified = userDocData?.verified_email !== undefined 
        ? userDocData.verified_email 
        : firebaseUser.emailVerified;
      
      // Create user data for our context
      const userData: User = {
        id: firebaseUser.uid,
        name: firebaseUser.displayName || userDocData.name || '',
        email: firebaseUser.email || '',
        picture: firebaseUser.photoURL || '',
        verified_email: isVerified,
        organizationId: userDocData.organizationId,
        role: userDocData.role || 'admin', // Default to 'admin' if not set (for new signups)
      };
      
      // Save to our context (pass organizationId; for multi-org use first org so GLBA session can be created)
      const loginOrgId = userDocData?.organizationId ?? userDocData?.organizationIds?.[0];
      await login(userData, loginOrgId);
      
      // Track authentication (signup if user doc didn't exist, login otherwise)
      if (!userDoc.exists()) {
        trackAuth.signup('google');
      } else {
        trackAuth.login('google');
      }
      
      // If email is not verified, redirect to verification page
      // Note: Google sign-ins typically have verified emails, but we check anyway
      // Invited users are treated as verified since invitation email already verified ownership
      if (!isVerified) {
        redirectToMainDomain('/verify-email');
      } else if (!userDocData.organizationId && !invitationToken) {
        // If user doesn't have an organization (both signup AND login), redirect to create one
        redirectToMainDomain('/organizations?newSignup=true');
      } else {
        redirectToMainDomain('/home');
      }
    } catch (error: any) {
      console.error('Error during Firebase authentication:', error);
      
      // Fallback to the original method if Firebase Auth fails
      try {
        console.log('Attempting fallback authentication method...');
        const payload = JSON.parse(atob(response.credential.split('.')[1]));
        
        // Process invitation in fallback mode if needed
        if (invitationToken && payload.email) {
          try {
            const invitationResult = await getInvitationByToken(invitationToken);
            if (invitationResult.success && invitationResult.invitation) {
              const invitation = invitationResult.invitation;
              if (invitation.email.toLowerCase() === payload.email.toLowerCase()) {
                // User matches invitation - could create user doc here if needed
                console.log('✅ Invitation matches user email:', payload.email);
              }
            }
          } catch (invError) {
            console.error('Error processing invitation in fallback:', invError);
          }
        }
        
        const userData: User = {
          id: payload.sub,
          name: payload.name,
          email: payload.email,
          picture: payload.picture,
          verified_email: payload.email_verified,
        };
        
        await login(userData, undefined); // organizationId not available in fallback mode
        trackAuth.login('google'); // Track fallback login
        redirectToMainDomain('/home');
      } catch (fallbackError) {
        console.error('Fallback authentication also failed:', fallbackError);
        alert('Authentication failed. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  }, [login, navigate, invitationToken, isSignUpMode]);

  // Add global error handler for OAuth errors
  useEffect(() => {
    const handleOAuthError = (error: any) => {
      console.error('OAuth error detected:', error);
      
      // Check for specific session/authentication errors
      if (error.type === 'session_timeout' || 
          error.message?.includes('session') ||
          error.message?.includes('expired') ||
          error.message?.includes('invalid')) {
        console.log('⚠️ OAuth session expired or invalid');
        logout();
        window.location.href = getLoginUrl();
      }
    };

    window.addEventListener('error', handleOAuthError);
    
    return () => {
      window.removeEventListener('error', handleOAuthError);
    };
  }, [logout, navigate]);

  useEffect(() => {
    const initializeGoogleAuth = () => {
      if (window.google && window.google.accounts && window.google.accounts.id) {
        try {
          window.google.accounts.id.initialize({
            client_id: import.meta.env.VITE_GOOGLE_CLIENT_ID || '48418907254-i72d2qnitld382irc9p5r75910cltmtt.apps.googleusercontent.com',
            callback: handleCredentialResponse,
            auto_select: false,
            cancel_on_tap_outside: true,
          });
          console.log('Google Auth initialized successfully');
        } catch (error) {
          console.error('Error initializing Google Auth:', error);
        }
      }
    };

    // Check if script is already loaded
    if (window.google && window.google.accounts) {
      initializeGoogleAuth();
      return;
    }

    // Load Google Identity Services script
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    
    script.onload = () => {
      console.log('Google script loaded');
      initializeGoogleAuth();
    };
    
    script.onerror = () => {
      console.error('Failed to load Google Identity Services script');
    };

    document.head.appendChild(script);

    return () => {
      // Cleanup
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, [handleCredentialResponse]);

  // Render Google button automatically when component mounts
  useEffect(() => {
    const renderGoogleButton = () => {
      const buttonDiv = document.getElementById('google-signin-button');
      if (buttonDiv && window.google && window.google.accounts && window.google.accounts.id) {
        try {
          window.google.accounts.id.renderButton(buttonDiv, {
            theme: 'outline',
            size: 'large',
            type: 'standard',
            shape: 'rectangular',
            text: 'signin_with',
            width: 250
          });
        } catch (error) {
          console.error('Error rendering Google button:', error);
        }
      }
    };

    // Render button after Google script loads
    if (window.google && window.google.accounts) {
      renderGoogleButton();
    } else {
      // Wait for script to load
      const checkGoogle = setInterval(() => {
        if (window.google && window.google.accounts) {
          renderGoogleButton();
          clearInterval(checkGoogle);
        }
      }, 100);

      // Cleanup interval after 10 seconds
      setTimeout(() => clearInterval(checkGoogle), 10000);
    }
  }, []);

  const handleLogout = () => {
    logout();
    if (window.google) {
      window.google.accounts.id.disableAutoSelect();
    }
    // Let ProtectedRoute handle the redirect automatically
  };

  if (isLoading) {
    return (
      <div className="flex items-center space-x-2">
        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600"></div>
        <span className="text-sm text-gray-600">Signing in...</span>
      </div>
    );
  }

  if (isAuthenticated && user) {
    return (
      <div className="flex items-center space-x-3">
        <div className="flex items-center space-x-2">
          <img
            src={user.picture}
            alt={user.name}
            className="w-8 h-8 rounded-full"
          />
          <div className="hidden sm:block">
            <p className="text-sm font-medium text-gray-900">{user.name}</p>
            <p className="text-xs text-gray-600">{user.email}</p>
          </div>
        </div>
        <button
          onClick={handleLogout}
          className="btn-secondary text-sm px-3 py-1"
        >
          Sign Out
        </button>
      </div>
    );
  }

  return (
    <div id="google-signin-button"></div>
  );
};

export default GoogleAuth;
